import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Grid, Box, IconButton, Tooltip } from "@mui/material";
import { useQuery, useQueryClient } from "react-query";
import { getJobExecutionsForVehicles } from "../../services/vehicle";
import { getDeploymentVehicleLogDetails } from "../../services/deployment";
import { getColumnsData } from "./VehicleDeploymentColumns";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";

const CustomToolbar = ({ onRefresh, isRefreshing }) => {
  return (
    <GridToolbarContainer sx={{ justifyContent: "flex-end" }}>
      <Tooltip title={isRefreshing ? "Refreshing..." : "Refresh"} placement="top">
        <span>
          <IconButton size="small" onClick={onRefresh} disabled={isRefreshing}>
            <RefreshIcon
              fontSize="small"
              color={isRefreshing ? "disabled" : "primary"}
            />
          </IconButton>
        </span>
      </Tooltip>
      <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
    </GridToolbarContainer>
  );
};

const VehicleDeploymentList = (props = {}) => {
  let { vehicleId, deploymentId } = useParams();
  const {
    vehicles = [],
    type,
    setErrMsg = () => {},
    setShowJobDocument,
  } = props;

  const [vehicle] = vehicles;
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const getFn =
    type === "vehicle"
      ? getJobExecutionsForVehicles
      : getDeploymentVehicleLogDetails;

  const queryArray = useMemo(
    () =>
      type === "vehicle"
        ? ["jobExecutionsForVehicle", deploymentId, vehicle?.vehicleKey]
        : ["vehicleLogDetails", deploymentId],
    [type, deploymentId, vehicle?.vehicleKey],
  );

  const columns = getColumnsData({ vehicleId, deploymentId, type });

  let { data: { data: allVehicles = [] } = {}, isLoading: summaryLoading } =
    useQuery(queryArray, getFn, {
      refetchOnWindowFocus: false,
      refetchInterval: 100000,
      retry: 0,
      onSuccess: () => {
        setShowJobDocument?.(true);
        setTimeout(() => setIsRefreshing(false), 2000); 
      },
      onError: (error) => {
        const { response: { data: { message = "" } = {} } = {} } = error;
        setErrMsg(message);
        setTimeout(() => setIsRefreshing(false), 2000);  
      },
    });

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    queryClient.invalidateQueries(queryArray);
  }, [queryClient, queryArray]);

  return (
    <Grid item xs={12}>
      <Box
        sx={
          type === "vehicle"
            ? { height: "auto", width: "100%" }
            : {}
        }
      >
        <DataGrid
          columnVisibilityModel={{
            vehicleKey: !vehicleId,
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: "createdAt", sort: "desc" }],
            },
          }}
          sx={
            type === "vehicle"
              ? { minHeight: 300, maxHeight: "calc(100vh - 232px)", height: "auto" }
              : { minHeight: 400 }
          }
          loading={summaryLoading}
          columns={columns}
          rows={allVehicles}
          paginationMode="client"
          getRowId={(row) => row.deploymentLogId || row.executionId}
          disableColumnMenu={false}
          {...(!vehicleId && {
            components: { Toolbar: CustomToolbar },
            componentsProps: {
              toolbar: {
                onRefresh: handleRefresh,
                isRefreshing,
              },
            },
          })}
        />
      </Box>
    </Grid>
  );
};

export default VehicleDeploymentList;